import { useState } from 'react';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import { Fields } from 'src/ui-component/FormikFormLibrary/FormikTypes';
import { useNavigate, useParams } from 'react-router-dom';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { Alert, Button, Typography } from '@mui/material';
import CostOfWork from './CostOfWork';
import { handleInsertSubcodeDocs } from '../uploadSubcodeDocs';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import {
  resetUccForm,
  updateMechanical
} from 'src/redux-store/slices/constructionFormSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';
import Summary from './Summary';
import getEnvVars from 'src/config/env';

// Interface for tab panel props
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Custom tab panel component
function CustomTabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Function to generate accessibility props for tabs
type A11yProps = (index: number) => { id: string; 'aria-controls': string };
const a11yProps: A11yProps = (index) => ({
  id: `simple-tab-${index}`,
  'aria-controls': `simple-tabpanel-${index}`
});

const MechanicalSubcode = ({
  applicationId,
  UCCFormShareID = 0,
  townshipID = 0,
  setExpanded,
  isGuest = false,
  fetchFormStatuses
}) => {
  const dispatch = useDispatch();
  const [value, setValue] = useState(0);
  const { applicationID, townshipId } = useParams();
  const userDetails = useSelector((store: RootState) => store.user.userDetails);
  const subCodesFormData = useSelector(
    (state: RootState) => state.constructionFormSlice.Mechanical
  );
  const { socketUrl } = getEnvVars();
  // Handles form field changes and updates the redux store
  const handleChange = (val: string, e: any) => {
    const { name } = e;
    dispatch(updateMechanical({ [name]: val }));
  };
  const navigate = useNavigate()

  // List of form fields
  const allFields: Fields[] = [
    {
      fieldName: 'ContractorName',
      initialValue: subCodesFormData?.ContractorName || '',
      fieldLabel: 'Contractor Name',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ContractorEmail',
      initialValue: subCodesFormData?.ContractorEmail || '',
      fieldLabel: 'Contractor Email Address',
      fieldType: 'email',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ContractorPhoneNumber',
      initialValue: subCodesFormData?.ContractorPhoneNumber || '',
      fieldLabel: 'Contractor Phone Number',
      fieldType: 'mobile',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ContractorAddress',
      initialValue: subCodesFormData?.ContractorAddress || '',
      fieldLabel: 'Contractor Address',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'FederalID',
      initialValue: subCodesFormData?.FederalID || '',
      fieldLabel: 'Federal Employer ID Number (EIN)',
      fieldType: 'number',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ContractorLicenseNumber',
      initialValue: subCodesFormData?.ContractorLicenseNumber || '',
      fieldLabel: 'Contractor License Number',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ConstractorLicenseExpirationDate',
      initialValue: subCodesFormData?.ConstractorLicenseExpirationDate || '',
      fieldLabel: 'Contractor License Expiration Date',
      required: true,
      fieldType: 'date',
      dependency: (val: string) =>
        dispatch(updateMechanical({ ConstractorLicenseExpirationDate: val }))
    },
    {
      fieldName: 'DescriptionOfWork',
      initialValue: subCodesFormData?.DescriptionOfWork || '',
      fieldLabel: 'Description of Work',
      fieldType: 'note',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'MechanicalDocumentFile',
      fieldLabel: 'Upload Document',
      initialValue: subCodesFormData?.MechanicalDocumentFile || '',
      fieldType: 'file',
      listUI: true,
      placement: 'start',
      dependency: handleChange,
      aboveParagraph: (
        <Typography>
          Please upload scanned copy or photo of the Federal Employer ID Number
          (EIN) license. Only image files (JPG, PNG, GIF, SVG, HEIC) and PDF
          files are allowed, with maximum file size of 10MB.
        </Typography>
      ),
      belowParagraph: subCodesFormData?.DocumentOfBuilderIdentity && (
        <a
          target="_blank"
          rel="noreferrer"
          href={socketUrl + subCodesFormData?.DocumentOfBuilderIdentity}
        >
          Federal Employer ID Number (EIN) license
        </a>
      )
    }
  ];

  async function insertMechanicalSubcode(payload: any) {
    const response = await httpRequest(
      'POST',
      `/construction/mechanical/ucc/${applicationId}`,
      payload
    );
    if (response?.status === 'success') {
      toast.success(`Mechanical UCC form created successfully.`);
      dispatch(
        updateMechanical({ UCCMechanicalID: response?.data?.UCCMechanicalID })
      );
      if (!townshipID) fetchFormStatuses();
      dispatch(resetUccForm());
      if (isGuest === true) {
        // For guest users, redirect to confirmation page
        // You may need to adjust the route based on your routing setup
        navigate('/confirmation', {
          state: {
            formType: 'Mechanical UCC Form',
            email: payload.email
          },
          replace: true // This prevents going back with the browser back button
        });
      } else {
        // For regular users, just reset the form
        setValue(0);
      }
      // setLoading(false);
    } else {
      // setLoading(false);
      toast.error(response?.message);
    }
  }

  async function updateMechanicalSubcode(payload: any) {
    const response = await httpRequest(
      'PATCH',
      `/construction/mechanical/ucc/${subCodesFormData?.UCCMechanicalID}/${
        applicationId || applicationID
      }`,
      payload
    );
    if (response?.status === 'success') {
      toast.success(`Mechanical UCC form updated successfully.`);
      dispatch(
        updateMechanical({ UCCMechanicalID: response?.data?.UCCMechanicalID })
      );
      if (!townshipID) fetchFormStatuses();
      dispatch(resetUccForm());
      setValue(0);
      // setLoading(false);
    } else {
      // setLoading(false);
      toast.error(response?.message);
    }
  }

  async function handleSaveForm() {
    let FileData = { FilePath: '' };
    if (subCodesFormData?.MechanicalDocumentFile) {
      FileData = await handleInsertSubcodeDocs(
        subCodesFormData?.MechanicalDocumentFile[0],
        'Construction',
        'Mechanical'
      );
    }

    const payload = {
      ContractorName: subCodesFormData?.ContractorName,
      ContractorEmail: subCodesFormData?.ContractorEmail,
      ContractorPhoneNumber: subCodesFormData?.ContractorPhoneNumber,
      ContractorAddress: subCodesFormData?.ContractorAddress,
      UtilityCompany: subCodesFormData?.UtilityCompany,
      FederalID: subCodesFormData?.FederalID,
      ContractorLicenseNumber: subCodesFormData?.ContractorLicenseNumber,
      ConstractorLicenseExpirationDate: subCodesFormData?.ConstractorLicenseExpirationDate,
      DocumentOfContractorIdentity: FileData?.FilePath,
      DescriptionOfWork: subCodesFormData?.DescriptionOfWork,

      SignatureImage: subCodesFormData?.SignatureImage,
      TotalFees: subCodesFormData?.getAllFee?.totalFee,
      TotalEstimateCost: subCodesFormData?.TotalEstimateCost,
      HeatingSystem: subCodesFormData?.HeatingSystem,
      HeatingSystemType: subCodesFormData?.HeatingSystemType,
      FuelType: subCodesFormData?.FuelType,

      SelfFilled: userDetails?.UserID ? true : false,
      UserID: userDetails?.UserID || 0,
      WorkItems: subCodesFormData?.WorkItems || [],
      TownshipID: townshipId || townshipID,
      UCCFormShareID: UCCFormShareID
    };
    if (subCodesFormData?.UCCMechanicalID) {
      updateMechanicalSubcode(payload);
    } else insertMechanicalSubcode(payload);
    setValue(2);
  }

  function onNext() {
    setValue(value + 1);
  }

  function onBack() {
    setValue(value - 1);
  }
  // Add this function after your existing handleChange function
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
  setValue(newValue);
  };
  
  return (
    <>
      <Box sx={{ width: '100%' }}>
        {/* Tabs */}
        <Tabs value={value}  onChange={handleTabChange} aria-label="Construction Permit Tabs">
          <Tab label="Mechanical Subcode" {...a11yProps(0)} />
          <Tab label="Cost of Work" {...a11yProps(1)} />
          <Tab label="Summary of Costs" {...a11yProps(2)} />
        </Tabs>

        <Alert severity="info" sx={{ mt: 2 }}>
          Provide information for whoever will be performing Mechanical work.
        </Alert>

        {/* Tab 1 - Mechanical Subcode Form */}
        <CustomTabPanel value={value} index={0}>
          <FormikForm
            allFields={allFields}
            defaultEnabledButton={['Save']}
            buttonTitle={['Cancel', 'Save']}
            heading={'Construction Permit'}
            buttonPlacement={'right'}
            formMethod={onNext}
            handleOtherButton={() => setExpanded(false)}
          />
        </CustomTabPanel>

        {/* Tab 2 - Estimated Cost of Work Form */}
        <CustomTabPanel value={value} index={1}>
          <CostOfWork
            onBack={onBack}
            onNext={onNext}
            townshipID={townshipID}
          />
        </CustomTabPanel>

        {/* Tab 3 - Summary of Costs */}
        <CustomTabPanel value={value} index={2}>
          <Summary />

          {/* Form Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 3
            }}
          >
            <Button onClick={onBack} variant="contained" color="inherit">
              Back
            </Button>
            <Button onClick={handleSaveForm} variant="contained">
              {subCodesFormData?.UCCMechanicalID ? 'Update' : 'Save'}
            </Button>
          </Box>
        </CustomTabPanel>
      </Box>
    </>
  );
};

// Add display name for debugging purposes
MechanicalSubcode.displayName = 'MechanicalSubcode';

export default MechanicalSubcode;
